import { useCallback, useState } from 'react'

export interface ConfirmDialogOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  severity?: 'warning' | 'error' | 'info' | 'success'
  confirmButtonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  cancelButtonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  confirmButtonVariant?: 'text' | 'outlined' | 'contained'
  cancelButtonVariant?: 'text' | 'outlined' | 'contained'
}

export interface ConfirmDialogState {
  isOpen: boolean
  options: ConfirmDialogOptions
  resolve: (value: boolean) => void
}

export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = useState<ConfirmDialogState | null>(null)

  const confirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        options: {
          title: 'Confirm Action',
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          severity: 'warning',
          confirmButtonColor: 'secondary',
          cancelButtonColor: 'secondary',
          confirmButtonVariant: 'contained',
          cancelButtonVariant: 'outlined',
          ...options,
        },
        resolve,
      })
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (dialogState) {
      dialogState.resolve(true)
      setDialogState(null)
    }
  }, [dialogState])

  const handleCancel = useCallback(() => {
    if (dialogState) {
      dialogState.resolve(false)
      setDialogState(null)
    }
  }, [dialogState])

  const handleClose = useCallback(() => {
    if (dialogState) {
      dialogState.resolve(false)
      setDialogState(null)
    }
  }, [dialogState])

  return {
    confirm,
    dialogState,
    handleConfirm,
    handleCancel,
    handleClose,
  }
}
